import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';
import { ENDPOINTS } from '../config/api';
import { setPasswordResetFlag, clearPasswordResetFlag } from '../lib/passwordResetFlag';

export type UserType = 'ADMIN' | 'OPERATOR' | 'COMPLIANCE' | 'EMPLOYEE' | 'CUSTOMER' | 'SUPPLIER';
export type ProviderCategory = 'BAAS' | 'CRYPTO' | 'CARDS' | 'BENEFITS';

export interface User {
  id: string;
  name: string;
  email: string;
  type: UserType;
  tenantId: string;
  tenantName?: string;
  isSuperAdmin: boolean;
  enabledCategories: ProviderCategory[];
  virtualAccountId?: string | null;
  cpfCnpj?: string;
  phone?: string;
  status?: string;
}

// Route mapping by user role
const DEFAULT_ROUTES: Record<string, string> = {
  SUPER_ADMIN: '/super-admin',
  ADMIN: '/admin',
  OPERATOR: '/admin',
  COMPLIANCE: '/admin',
  EMPLOYEE: '/banking/employee',
  CUSTOMER: '/banking/customer',
  SUPPLIER: '/banking/supplier',
};

// Profile-specific routes for correntistas
const PROFILE_ROUTES: Record<string, string> = {
  CUSTOMER: '/banking/customer',
  SUPPLIER: '/banking/supplier',
  EMPLOYEE: '/banking/employee',
};

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  
  // Actions
  login: (email: string, password: string, tenantId: string, turnstileToken?: string) => Promise<{ requires2FA?: boolean; tempToken?: string } | void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
  setAuth: (accessToken: string, refreshToken: string, user: User) => void;
  fetchUser: () => Promise<void>;
  initialize: () => Promise<void>;
  
  // Computed helpers
  getDefaultRoute: () => string;
  getProfileRoute: () => string;
  isSuperAdmin: () => boolean;
  isTenantAdmin: () => boolean;
  isCorrentista: () => boolean;
  hasCategory: (category: ProviderCategory) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      error: null,

      initialize: async () => {
        const { accessToken, isInitialized } = get();
        
        // Don't initialize twice
        if (isInitialized) return;
        
        // If no token, mark as initialized and not authenticated
        if (!accessToken) {
          set({ isInitialized: true, isAuthenticated: false });
          return;
        }

        // Validate token by fetching user
        try {
          const response = await api.get(ENDPOINTS.auth.me);
          const userData = response.data;
          
          const user: User = {
            id: userData.id,
            name: userData.name,
            email: userData.email,
            type: userData.type,
            tenantId: userData.tenantId,
            tenantName: userData.tenant?.name,
            isSuperAdmin: userData.isSuperAdmin || false,
            enabledCategories: userData.tenant?.enabledCategories || ['BAAS'],
            virtualAccountId: userData.virtualAccountId || null,
            cpfCnpj: userData.cpfCnpj,
            phone: userData.phone,
            status: userData.status,
          };

          set({ user, isAuthenticated: true, isInitialized: true });
        } catch {
          // Token invalid - clear everything including persist storage
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('auth-storage');
          delete api.defaults.headers.common['Authorization'];
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isInitialized: true,
          });
        }
      },

      login: async (email: string, password: string, tenantId: string, turnstileToken?: string) => {
        // Clear any stale auth state before attempting login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('auth-storage');
        
        set({ isLoading: true, error: null, accessToken: null, refreshToken: null, isAuthenticated: false });
        
        try {
          const response = await api.post(ENDPOINTS.auth.login, { email, password, tenantId, turnstileToken });
          const { accessToken, refreshToken, requirePasswordReset } = response.data;

          // Save tokens to localStorage FIRST so the interceptor can use them
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);
          
          // Also set the Authorization header directly on the axios instance
          // to avoid any race condition with the interceptor reading from localStorage
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

          // Security Hardening Phase 1: signal the router to force the
          // forced-password-reset page until the user changes their password.
          if (requirePasswordReset) {
            setPasswordResetFlag();
          } else {
            clearPasswordResetFlag();
          }

          set({
            accessToken,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
          });

          // Fetch user data from /auth/me
          await get().fetchUser();
        } catch (error: unknown) {
          const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao fazer login';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      fetchUser: async () => {
        try {
          const response = await api.get(ENDPOINTS.auth.me);
          const userData = response.data;
          
          const user: User = {
            id: userData.id,
            name: userData.name,
            email: userData.email,
            type: userData.type,
            tenantId: userData.tenantId,
            tenantName: userData.tenant?.name,
            isSuperAdmin: userData.isSuperAdmin || false,
            enabledCategories: userData.tenant?.enabledCategories || ['BAAS'],
            virtualAccountId: userData.virtualAccountId || null,
            cpfCnpj: userData.cpfCnpj,
            phone: userData.phone,
            status: userData.status,
          };

          set({ user });
        } catch (error) {
          console.error('Erro ao buscar dados do usuário:', error);
          // Don't auto-logout here - let the API interceptor handle 401s
          // This prevents race conditions and infinite loops
        }
      },

      logout: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('auth-storage');
        delete api.defaults.headers.common['Authorization'];
        clearPasswordResetFlag();
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          // Keep isInitialized true to prevent re-initialization
        });
      },

      refreshAuth: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return;

        try {
          const response = await api.post(ENDPOINTS.auth.refresh, { refreshToken });
          const { accessToken, refreshToken: newRefreshToken } = response.data;

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

          set({ accessToken, refreshToken: newRefreshToken });
        } catch {
          get().logout();
        }
      },

      clearError: () => set({ error: null }),

      setUser: (user: User) => set({ user }),

      setAuth: (accessToken: string, refreshToken: string, user: User) => {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        set({
          accessToken,
          refreshToken,
          user,
          isAuthenticated: true,
          isLoading: false,
          isInitialized: true,
        });
      },

      // Helper to get default route based on user role
      getDefaultRoute: () => {
        const { user } = get();
        if (!user) return '/login';
        
        if (user.isSuperAdmin) {
          return DEFAULT_ROUTES.SUPER_ADMIN;
        }
        
        return DEFAULT_ROUTES[user.type] || '/admin';
      },

      // Helper to get profile-specific route for correntistas
      getProfileRoute: () => {
        const { user } = get();
        if (!user) return '/banking/customer';
        
        // Super admin defaults to customer profile
        if (user.isSuperAdmin) {
          return '/banking/customer';
        }
        
        return PROFILE_ROUTES[user.type] || '/banking/customer';
      },

      // Check if user is Super Admin
      isSuperAdmin: () => {
        const { user } = get();
        return user?.isSuperAdmin || false;
      },

      // Check if user is Tenant Admin (ADMIN, OPERATOR, COMPLIANCE)
      isTenantAdmin: () => {
        const { user } = get();
        if (!user) return false;
        return ['ADMIN', 'OPERATOR', 'COMPLIANCE'].includes(user.type);
      },

      // Check if user is Correntista (CUSTOMER, SUPPLIER, EMPLOYEE)
      isCorrentista: () => {
        const { user } = get();
        if (!user) return false;
        return ['CUSTOMER', 'SUPPLIER', 'EMPLOYEE'].includes(user.type);
      },

      // Check if tenant has a specific category enabled
      // Note: This is a legacy check. With Provider Engine, categories are determined
      // by the ProviderInstances configured for the tenant. For now, we keep backward
      // compatibility but Super Admins always have full access.
      hasCategory: (category: ProviderCategory) => {
        const { user } = get();
        if (!user) return false;
        // Super admins have access to all categories
        if (user.isSuperAdmin) return true;
        // For regular users, check enabledCategories (legacy) or allow all if empty
        // When enabledCategories is empty, it means the tenant uses Provider Engine
        // and categories are determined by configured ProviderInstances
        if (!user.enabledCategories || user.enabledCategories.length === 0) {
          return true; // Allow - actual access is controlled by ProviderInstances
        }
        return user.enabledCategories.includes(category);
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
